import React, { useState } from "react";
import { useNavigate } from "react-router-dom";


import heroImg from "../../assets/page-banner-img.jpg";

/* ─────────────────────────────────────────────
   RESPONSIVE CSS
───────────────────────────────────────────── */
const RESPONSIVE_CSS = `
  .atc-hero-title {
    font-size: clamp(16px, 3vw, 30px) !important;
    line-height: 1.3 !important;
  }
  .atc-hero-overlay {
    padding-left: clamp(16px, 5vw, 70px) !important;
    padding-right: 16px !important;
  }
  .atc-hero-img {
    height: clamp(180px, 32vw, 420px) !important;
  }
  .atc-main-wrap {
    width: 94% !important;
    padding: 18px !important;
  }
  .atc-banner-heading {
    font-size: clamp(20px, 4.5vw, 35px) !important;
  }
  .atc-layout {
    grid-template-columns: 1fr !important;
  }
  .atc-sidebar {
    position: static !important;
  }
  .atc-section-title {
    font-size: clamp(15px, 3vw, 22px) !important;
  }
  .atc-meta-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 8px !important;
  }
  .atc-nav-row {
    flex-direction: column !important;
  }
  .atc-table {
    min-width: 600px !important;
  }
  .atc-th, .atc-td {
    padding: 10px !important;
    font-size: 13px !important;
  }

  @media (max-width: 900px) {
    .atc-banner-box {
      padding: 18px !important;
      border-radius: 16px !important;
    }
    .atc-sidebar {
      padding: 18px !important;
      border-radius: 14px !important;
    }
    .atc-accordion-head {
      padding: 16px !important;
      gap: 12px !important;
    }
    .atc-diamond {
      width: 36px !important;
      height: 36px !important;
    }
  }

  @media (max-width: 600px) {
    .atc-main-wrap {
      width: 100% !important;
      padding: 12px !important;
      margin: 16px auto !important;
    }
    .atc-banner-box {
      padding: 14px !important;
      border-radius: 12px !important;
    }
    .atc-accordion-head {
      padding: 12px !important;
      gap: 10px !important;
    }
    .atc-section-title {
      font-size: 14px !important;
    }
    .atc-table {
      min-width: 520px !important;
    }
    .atc-th, .atc-td {
      padding: 8px !important;
      font-size: 12px !important;
    }
    .atc-download-btn {
      font-size: 12px !important;
      padding: 7px 14px !important;
    }
    .atc-prev-next-box {
      padding: 18px !important;
    }
  }
`;

/* ─────────────────────────────────────────────
   DATA: all 44 sections
───────────────────────────────────────────── */
const catalogSections = [
  {
    number: 1,
    title: "Foundational AI & Machine Learning Frameworks",
    rows: [
      { technology: "TensorFlow", useCase: "Open-source ML framework by Google for deep learning and neural networks." },
      { technology: "PyTorch", useCase: "Facebook's dynamic deep learning library." },
      { technology: "Keras", useCase: "High-level API for neural networks." },
      { technology: "Scikit-learn", useCase: "Classical ML library for regression, classification, and clustering." },
      { technology: "JAX", useCase: "Google's high-performance numerical computing library for ML research." },
      { technology: "MXNet", useCase: "Scalable deep learning framework supporting multiple languages." },
      { technology: "Caffe", useCase: "Deep learning framework optimized for speed and modularity." },
      { technology: "Theano", useCase: "Early deep learning library for numerical computation." },
      { technology: "ONNX (Open Neural Network Exchange)", useCase: "Standard for model interoperability." },
      { technology: "Hugging Face Transformers", useCase: "Leading library for pre-trained NLP models (BERT, GPT, etc.)." },
    ],
  },
  {
    number: 2,
    title: "Large Language Models (LLMs) & Generative AI",
    rows: [
      { technology: "GPT-4 (OpenAI)", useCase: "State-of-the-art LLM for text generation, reasoning, and multimodal tasks." },
      { technology: "GPT-3.5", useCase: "Earlier OpenAI model powering ChatGPT and many applications." },
      { technology: "LLaMA (Meta)", useCase: "Open-weight LLM for research and commercial use." },
      { technology: "BERT (Google)", useCase: "Transformer model for NLP tasks like search and Q&A." },
      { technology: "T5 (Google)", useCase: "Text-to-text transfer transformer for summarization and translation." },
      { technology: "PaLM 2 (Google)", useCase: "Large LLM for reasoning, multilingual, and coding tasks." },
      { technology: "Claude (Anthropic)", useCase: "AI assistant focused on safety and alignment." },
      { technology: "Falcon (TII UAE)", useCase: "Open-source LLM for commercial and research use." },
      { technology: "Mistral 7B", useCase: "Efficient open-weight LLM for text generation." },
      { technology: "BLOOM (BigScience)", useCase: "Multilingual open-source LLM." },
      { technology: "Stable Diffusion (Stability AI)", useCase: "Image generation via diffusion models." },
      { technology: "DALL·E (OpenAI)", useCase: "Text-to-image generation model." },
      { technology: "MidJourney", useCase: "AI art generation via Discord-based diffusion models." },
      { technology: "Imagen (Google)", useCase: "High-fidelity text-to-image model." },
      { technology: "Whisper (OpenAI)", useCase: "Speech-to-text transcription model." },
      { technology: "VALL-E (Microsoft)", useCase: "Neural codec language model for voice synthesis." },
      { technology: "Codex (OpenAI)", useCase: "Powers GitHub Copilot for AI-assisted coding." },
      { technology: "CodeLlama (Meta)", useCase: "LLM specialized for code generation." },
      { technology: "Orca 2 (Microsoft)", useCase: "Small but efficient reasoning-focused LLM." },
      { technology: "Gemini (Google DeepMind)", useCase: "Multimodal AI rivaling GPT-4." },
    ],
  },
  {
    number: 3,
    title: "AI Agent & Automation Tools",
    rows: [
      { technology: "AutoGPT", useCase: "Autonomous AI agent for task automation." },
      { technology: "BabyAGI", useCase: "Task-driven autonomous AI agent." },
      { technology: "LangChain", useCase: "Framework for building LLM-powered applications." },
      { technology: "LlamaIndex", useCase: "Data indexing for LLM-based retrieval-augmented generation." },
      { technology: "Semantic Kernel (Microsoft)", useCase: "AI orchestration for agents and plugins." },
      { technology: "Hugging Face Agents", useCase: "Tools for deploying LLM-based agents." },
      { technology: "Pinecone", useCase: "Vector database for AI agent memory." },
      { technology: "Weaviate", useCase: "Open-source vector search engine for AI applications." },
      { technology: "Milvus", useCase: "Scalable vector database for similarity search." },
      { technology: "Chroma", useCase: "Lightweight embedding database for LLMs." },
      { technology: "Haystack", useCase: "Framework for building search-based AI applications." },
      { technology: "Rasa", useCase: "Open-source conversational AI for chatbots." },
      { technology: "Dialogflow (Google)", useCase: "Cloud-based NLP for chatbots." },
      { technology: "Microsoft Bot Framework", useCase: "Tools for building enterprise chatbots." },
      { technology: "AutoML (Google, Azure, AWS)", useCase: "Automated machine learning pipelines." },
      { technology: "Roboflow", useCase: "Computer vision dataset management." },
      { technology: "Replicate", useCase: "Cloud API for running open-source ML models." },
      { technology: "CrewAI", useCase: "Framework for multi-agent collaboration." },
      { technology: "SuperAGI", useCase: "Open-source autonomous AI agent platform." },
      { technology: "GPT Engineer", useCase: "AI agent for code generation from prompts." },
    ],
  },
  {
    number: 4,
    title: "AI Deployment & MLOps",
    rows: [
      { technology: "MLflow", useCase: "Open-source platform for managing ML lifecycle." },
      { technology: "Kubeflow", useCase: "Kubernetes-based ML workflows." },
      { technology: "TFX (TensorFlow Extended)", useCase: "Production ML pipelines." },
      { technology: "Seldon Core", useCase: "Model deployment on Kubernetes." },
      { technology: "BentoML", useCase: "Model serving and deployment." },
      { technology: "Ray", useCase: "Distributed computing for AI/ML workloads." },
      { technology: "FastAPI", useCase: "Python framework for deploying ML models." },
      { technology: "Flask", useCase: "Lightweight web framework for model APIs." },
      { technology: "Docker", useCase: "Containerization for AI model deployment." },
      { technology: "Kubernetes", useCase: "Orchestration for scalable AI workloads." },
      { technology: "AWS SageMaker", useCase: "Managed ML service on AWS." },
      { technology: "Google Vertex AI", useCase: "Unified AI platform on GCP." },
      { technology: "Azure ML", useCase: "Microsoft's cloud-based ML service." },
      { technology: "Databricks", useCase: "Unified analytics for AI workflows." },
      { technology: "Snowflake", useCase: "Cloud data warehousing for AI." },
      { technology: "Neptune.ai", useCase: "Experiment tracking for ML models." },
      { technology: "Weights & Biases", useCase: "ML experiment tracking and visualization." },
      { technology: "Comet.ml", useCase: "ML model monitoring and management." },
      { technology: "Evidently AI", useCase: "Monitoring for model drift and performance." },
      { technology: "Arize AI", useCase: "AI observability platform." },
    ],
  },
  {
    number: 5,
    title: "Computer Vision & Multimodal AI",
    rows: [
      { technology: "OpenCV", useCase: "Open-source computer vision library." },
      { technology: "YOLO (Ultralytics)", useCase: "Real-time object detection." },
      { technology: "Detectron2 (Facebook)", useCase: "Object detection and segmentation." },
      { technology: "MMDetection", useCase: "OpenMMLab's object detection toolbox." },
      { technology: "EfficientDet", useCase: "Scalable object detection model." },
      { technology: "CLIP (OpenAI)", useCase: "Vision-language model for image-text matching." },
      { technology: "Segment Anything (Meta)", useCase: "Zero-shot image segmentation." },
      { technology: "Stable Video Diffusion", useCase: "Video generation from images." },
      { technology: "Pika Labs", useCase: "AI-powered video generation." },
      { technology: "Runway ML", useCase: "Creative AI tools for video and image editing." },
      { technology: "StyleGAN (NVIDIA)", useCase: "High-quality synthetic image generation." },
      { technology: "DeepFace", useCase: "Facial recognition and analysis." },
      { technology: "MediaPipe (Google)", useCase: "Real-time multimedia processing." },
      { technology: "Albumentations", useCase: "Image augmentation library." },
      { technology: "DINOv2 (Meta)", useCase: "Self-supervised vision model." },
      { technology: "BLIP-2", useCase: "Vision-language pre-training for multimodal tasks." },
      { technology: "FLAVA (Meta)", useCase: "Multimodal (text + image) understanding." },
      { technology: "Kosmos (Microsoft)", useCase: "Multimodal LLM for text, image, and audio." },
      { technology: "Pix2Pix", useCase: "Image-to-image translation with GANs." },
      { technology: "CycleGAN", useCase: "Unpaired image-to-image translation." },
    ],
  },
  {
    number: 6,
    title: "Reinforcement Learning & Robotics",
    rows: [
      { technology: "OpenAI Gym", useCase: "Toolkit for RL algorithm development." },
      { technology: "Stable Baselines3", useCase: "RL algorithms for training agents." },
      { technology: "Ray RLlib", useCase: "Scalable reinforcement learning." },
      { technology: "Unity ML-Agents", useCase: "RL in 3D environments." },
      { technology: "Isaac Gym (NVIDIA)", useCase: "Robotics simulation for RL." },
      { technology: "MuJoCo", useCase: "Physics engine for robotics research." },
      { technology: "DeepMind Control Suite", useCase: "Benchmarking for RL agents." },
      { technology: "ROS (Robot Operating System)", useCase: "Framework for robotics." },
      { technology: "PyBullet", useCase: "Physics simulation for robotics and RL." },
      { technology: "D4RL", useCase: "Datasets for offline reinforcement learning." },
    ],
  },
  {
    number: 7,
    title: "NLP & Speech Processing",
    rows: [
      { technology: "spaCy", useCase: "Industrial-strength NLP library." },
      { technology: "NLTK", useCase: "Classic NLP toolkit for text processing." },
      { technology: "Gensim", useCase: "Topic modeling and word embeddings." },
      { technology: "FastText (Facebook)", useCase: "Efficient text classification." },
      { technology: "AllenNLP", useCase: "Research-focused NLP library." },
      { technology: "Flair", useCase: "NLP with contextual embeddings." },
      { technology: "Stanford NLP", useCase: "Java-based NLP tools." },
      { technology: "DeepSpeech (Mozilla)", useCase: "Open-source speech recognition." },
      { technology: "Wav2Vec (Facebook)", useCase: "Self-supervised speech recognition." },
      { technology: "VITS", useCase: "End-to-end TTS (Text-to-Speech) synthesis." },
    ],
  },
  {
    number: 8,
    title: "Graph AI & Knowledge Graphs",
    rows: [
      { technology: "Neo4j", useCase: "Leading graph database for knowledge graphs." },
      { technology: "DGL (Deep Graph Library)", useCase: "Graph neural networks (GNNs) for structured data." },
      { technology: "PyTorch Geometric", useCase: "Library for deep learning on graphs." },
      { technology: "NetworkX", useCase: "Python library for graph analysis." },
      { technology: "StellarGraph", useCase: "Machine learning on graphs." },
      { technology: "GraphSAGE", useCase: "Inductive representation learning on graphs." },
      { technology: "GAT (Graph Attention Networks)", useCase: "Attention-based graph learning." },
      { technology: "KG-BERT", useCase: "Knowledge graph completion using BERT." },
      { technology: "RDFLib", useCase: "Python library for RDF (Resource Description Framework)." },
      { technology: "Apache Jena", useCase: "Framework for building semantic web and linked data apps." },
    ],
  },
  {
    number: 9,
    title: "Edge AI & On-Device ML",
    rows: [
      { technology: "TensorFlow Lite", useCase: "Lightweight ML for mobile and edge devices." },
      { technology: "Core ML (Apple)", useCase: "On-device AI for iOS apps." },
      { technology: "ML Kit (Google)", useCase: "Mobile ML SDK for Android/iOS." },
      { technology: "ONNX Runtime", useCase: "Cross-platform inference engine." },
      { technology: "OpenVINO (Intel)", useCase: "Optimized AI for Intel hardware." },
      { technology: "TFLite Micro", useCase: "TinyML for microcontrollers." },
      { technology: "NVIDIA Jetson", useCase: "AI at the edge with GPU acceleration." },
      { technology: "Coral (Google)", useCase: "Edge TPU for fast on-device inference." },
      { technology: "Qualcomm AI Engine", useCase: "AI acceleration on Snapdragon chips." },
      { technology: "DeepStream (NVIDIA)", useCase: "AI-powered video analytics at the edge." },
    ],
  },
  {
    number: 10,
    title: "Quantum Machine Learning",
    rows: [
      { technology: "PennyLane", useCase: "Quantum ML library with PyTorch/TF integration." },
      { technology: "Qiskit (IBM)", useCase: "Quantum computing framework with ML modules." },
      { technology: "Cirq (Google)", useCase: "Quantum circuit simulator for ML research." },
      { technology: "TensorFlow Quantum", useCase: "Hybrid quantum-classical ML." },
      { technology: "Xanadu's Strawberry Fields", useCase: "Quantum photonic ML." },
    ],
  },
  {
    number: 11,
    title: "Synthetic Data & Data Augmentation",
    rows: [
      { technology: "Synthetic Data Vault (SDV)", useCase: "Open-source synthetic data generation." },
      { technology: "Gretel.ai", useCase: "Privacy-preserving synthetic data." },
      { technology: "CTGAN", useCase: "Synthetic tabular data generation." },
      { technology: "AugLy (Facebook)", useCase: "Multimodal data augmentation." },
      { technology: "Imgaug", useCase: "Image augmentation for CV." },
    ],
  },
  {
    number: 12,
    title: "Explainable AI (XAI) & Interpretability",
    rows: [
      { technology: "SHAP", useCase: "Model-agnostic explainability via Shapley values." },
      { technology: "LIME", useCase: "Local interpretable model-agnostic explanations." },
      { technology: "Captum (PyTorch)", useCase: "Model interpretability library." },
      { technology: "Alibi", useCase: "Tools for ML model inspection." },
      { technology: "What-If Tool (Google)", useCase: "Interactive model analysis." },
    ],
  },
  {
    number: 13,
    title: "AI for Science & Healthcare",
    rows: [
      { technology: "AlphaFold (DeepMind)", useCase: "Protein structure prediction." },
      { technology: "ESMFold", useCase: "Meta's protein folding model." },
      { technology: "BioGPT", useCase: "LLM for biomedical text generation." },
      { technology: "MONAI", useCase: "Medical imaging AI framework." },
      { technology: "DeepChem", useCase: "Drug discovery with deep learning." },
    ],
  },
  {
    number: 14,
    title: "AI for Finance & Trading",
    rows: [
      { technology: "QuantConnect", useCase: "Algorithmic trading with ML." },
      { technology: "Zipline", useCase: "Backtesting trading algorithms." },
      { technology: "PyFolio", useCase: "Portfolio analysis for quant finance." },
      { technology: "TensorTrade", useCase: "RL for trading strategies." },
      { technology: "FinRL", useCase: "Deep RL for financial markets." },
    ],
  },
  {
    number: 15,
    title: "AI Ethics & Bias Mitigation",
    rows: [
      { technology: "Fairlearn", useCase: "Tools for assessing fairness in AI." },
      { technology: "AI Fairness 360 (IBM)", useCase: "Bias detection and mitigation." },
      { technology: "Debias (Hugging Face)", useCase: "Reducing bias in NLP models." },
      { technology: "DALL·E 2 Moderation", useCase: "Filtering harmful AI-generated content." },
      { technology: "Holistic AI", useCase: "Governance and risk management." },
    ],
  },
  {
    number: 16,
    title: "AI in Gaming & Simulation",
    rows: [
      { technology: "Unity ML-Agents", useCase: "RL for game AI." },
      { technology: "OpenAI Five", useCase: "AI for competitive gaming (Dota 2)." },
      { technology: "AlphaStar (DeepMind)", useCase: "StarCraft II-playing AI." },
      { technology: "Procgen", useCase: "Procedural content generation." },
      { technology: "Godot ML", useCase: "Machine learning in game engines." },
    ],
  },
  {
    number: 17,
    title: "AI for Cybersecurity",
    rows: [
      { technology: "Darktrace", useCase: "AI-powered threat detection." },
      { technology: "Cylance", useCase: "ML-based antivirus." },
      { technology: "IBM Watson for Cybersecurity", useCase: "NLP for threat intelligence." },
      { technology: "Deepsound", useCase: "AI for audio steganography detection." },
      { technology: "Malwarebytes AI", useCase: "ML-driven malware detection." },
    ],
  },
  {
    number: 18,
    title: "AI for Climate & Sustainability",
    rows: [
      { technology: "ClimateNet", useCase: "AI for climate modeling." },
      { technology: "Watsonx.ai (IBM)", useCase: "AI for environmental insights." },
      { technology: "Google Earth Engine", useCase: "AI for satellite imagery analysis." },
      { technology: "Ocean Cleanup AI", useCase: "ML for pollution tracking." },
      { technology: "Carbon Tracker", useCase: "AI for emissions monitoring." },
    ],
  },
  {
    number: 19,
    title: "AI Hardware & Accelerators",
    rows: [
      { technology: "NVIDIA A100/H100", useCase: "GPUs for AI training." },
      { technology: "Google TPU", useCase: "Custom AI accelerators." },
      { technology: "Cerebras Wafer-Scale Engine", useCase: "Massive AI chips." },
      { technology: "Graphcore IPU", useCase: "Processor for graph-based ML." },
      { technology: "Groq LPU", useCase: "Low-latency AI inference chip." },
    ],
  },
  {
    number: 20,
    title: "AI for Creativity & Art",
    rows: [
      { technology: "AIVA", useCase: "AI music composition." },
      { technology: "Jukebox (OpenAI)", useCase: "Neural net for music generation." },
      { technology: "Magenta (Google)", useCase: "AI for art and music." },
      { technology: "Artbreeder", useCase: "Generative art via GANs." },
      { technology: "Runway ML", useCase: "AI-powered video editing." },
    ],
  },
  {
    number: 21,
    title: "AI in Autonomous Vehicles",
    rows: [
      { technology: "Waymo", useCase: "Self-driving AI by Google." },
      { technology: "Tesla Autopilot", useCase: "Vision-based autonomous driving." },
      { technology: "Apollo (Baidu)", useCase: "Open-source autonomous driving." },
      { technology: "CARLA", useCase: "Simulation for autonomous vehicle training." },
      { technology: "NVIDIA Drive", useCase: "AI platform for self-driving cars." },
    ],
  },
  {
    number: 22,
    title: "AI for Legal & Compliance",
    rows: [
      { technology: "ROSS Intelligence", useCase: "AI legal research assistant." },
      { technology: "Harvey AI", useCase: "Legal document analysis." },
      { technology: "Kira Systems", useCase: "Contract review AI." },
      { technology: "Luminance", useCase: "AI for due diligence." },
      { technology: "DoNotPay", useCase: "AI-powered legal chatbot." },
    ],
  },
  {
    number: 23,
    title: "AI for Retail & E-Commerce",
    rows: [
      { technology: "Dynamic Yield (McDonald's)", useCase: "Personalization AI." },
      { technology: "ViSenze", useCase: "Visual search for shopping." },
      { technology: "Recombinant", useCase: "AI-powered product recommendations." },
      { technology: "Coveo", useCase: "E-commerce search relevance." },
      { technology: "Syte", useCase: "Visual AI for fashion." },
    ],
  },
  {
    number: 24,
    title: "AI for HR & Recruitment",
    rows: [
      { technology: "Pymetrics", useCase: "AI-driven hiring assessments." },
      { technology: "HireVue", useCase: "AI video interviews." },
      { technology: "Textio", useCase: "AI for bias-free job descriptions." },
      { technology: "Eightfold.ai", useCase: "Talent intelligence platform." },
      { technology: "Beamery", useCase: "AI for candidate engagement." },
    ],
  },
  {
    number: 25,
    title: "AI for Agriculture",
    rows: [
      { technology: "Blue River Tech (John Deere)", useCase: "AI for precision farming." },
      { technology: "FarmWise", useCase: "Autonomous weeding robots." },
      { technology: "Prospera", useCase: "AI crop monitoring." },
      { technology: "Taranis", useCase: "AI for pest detection." },
      { technology: "Plantix", useCase: "Disease diagnosis via ML." },
    ],
  },
  {
    number: 26,
    title: "AI for Education",
    rows: [
      { technology: "Duolingo Max", useCase: "GPT-4 for language learning." },
      { technology: "Khanmigo (Khan Academy)", useCase: "AI tutor." },
      { technology: "Gradescope", useCase: "AI for grading assignments." },
      { technology: "Cognii", useCase: "AI for essay scoring." },
      { technology: "Squirrel AI", useCase: "Adaptive learning platform." },
    ],
  },
  {
    number: 27,
    title: "AI for Customer Support",
    rows: [
      { technology: "Zendesk Answer Bot", useCase: "AI-powered customer service." },
      { technology: "Intercom Fin", useCase: "GPT-4 for support automation." },
      { technology: "Forethought", useCase: "AI for ticket routing." },
      { technology: "Ada", useCase: "Chatbot for customer service." },
      { technology: "Ultimate.ai", useCase: "Conversational AI for support." },
    ],
  },
  {
    number: 28,
    title: "AI for Social Media",
    rows: [
      { technology: "Hootsuite Insights", useCase: "AI for social listening." },
      { technology: "Clarifai", useCase: "Image/Video moderation." },
      { technology: "Sprout Social", useCase: "AI-driven analytics." },
      { technology: "Lately", useCase: "AI for content scheduling." },
      { technology: "Phrasee", useCase: "AI-generated social copy." },
    ],
  },
  {
    number: 29,
    title: "AI for Manufacturing",
    rows: [
      { technology: "Siemens MindSphere", useCase: "AI for predictive maintenance." },
      { technology: "PTC ThingWorx", useCase: "Industrial IoT with AI." },
      { technology: "C3 AI", useCase: "Enterprise AI for manufacturing." },
      { technology: "Falkonry", useCase: "AI for anomaly detection." },
      { technology: "Augury", useCase: "Machine health monitoring." },
    ],
  },
  {
    number: 30,
    title: "AI for Space & Astronomy",
    rows: [
      { technology: "NASA Frontier Development Lab", useCase: "AI for space exploration." },
      { technology: "Google Sky", useCase: "AI for celestial object detection." },
      { technology: "SETI@home", useCase: "Distributed AI for extraterrestrial signals." },
      { technology: "AstroNet (Google)", useCase: "Exoplanet discovery via ML." },
      { technology: "SpaceML", useCase: "Open-source AI for space research." },
    ],
  },
  {
    number: 31,
    title: "AI for Sports Analytics",
    rows: [
      { technology: "Second Spectrum", useCase: "AI for basketball analytics." },
      { technology: "Hawk-Eye", useCase: "AI for ball tracking in sports." },
      { technology: "Stats Perform", useCase: "AI-driven sports predictions." },
      { technology: "Zone7", useCase: "AI for injury prevention." },
      { technology: "Catapult Sports", useCase: "Wearable AI for athletes." },
    ],
  },
  {
    number: 32,
    title: "AI for Government & Public Sector",
    rows: [
      { technology: "Palantir Gotham", useCase: "AI for defense and intelligence." },
      { technology: "C3.ai Gov", useCase: "AI for public sector analytics." },
      { technology: "Predictive Policing (PredPol)", useCase: "Crime forecasting AI." },
      { technology: "BlueDot", useCase: "AI for pandemic tracking." },
      { technology: "Citibeats", useCase: "AI for social policy insights." },
    ],
  },
  {
    number: 33,
    title: "AI for Energy & Utilities",
    rows: [
      { technology: "DeepMind for Grid Optimization", useCase: "AI for energy efficiency." },
      { technology: "C3 AI for Energy", useCase: "Predictive maintenance for utilities." },
      { technology: "AutoGrid", useCase: "AI for smart grid management." },
      { technology: "Uptake", useCase: "AI for industrial asset performance." },
      { technology: "SparkCognition", useCase: "AI for renewable energy." },
    ],
  },
  {
    number: 34,
    title: "AI for Logistics & Supply Chain",
    rows: [
      { technology: "ClearMetal", useCase: "AI for logistics optimization." },
      { technology: "FourKites", useCase: "AI for real-time shipment tracking." },
      { technology: "Locus Robotics", useCase: "Warehouse automation AI." },
      { technology: "Project44", useCase: "AI for supply chain visibility." },
      { technology: "Noodle.ai", useCase: "AI for inventory management." },
    ],
  },
  {
    number: 35,
    title: "AI for Mental Health",
    rows: [
      { technology: "Woebot", useCase: "AI therapy chatbot." },
      { technology: "Wysa", useCase: "Mental health AI assistant." },
      { technology: "Replika", useCase: "AI companion chatbot." },
      { technology: "Ellie (USC ICT)", useCase: "Virtual therapist." },
      { technology: "Ginger", useCase: "AI for behavioral health." },
    ],
  },
  {
    number: 36,
    title: "AI for Audio & Speech",
    rows: [
      { technology: "Suno AI", useCase: "AI music generation." },
      { technology: "Descript", useCase: "AI-powered audio editing." },
      { technology: "Resemble AI", useCase: "Voice cloning and synthesis." },
      { technology: "Murf.ai", useCase: "Text-to-speech for videos." },
      { technology: "Voicemod", useCase: "Real-time voice modulation." },
    ],
  },
  {
    number: 37,
    title: "AI for 3D & AR/VR",
    rows: [
      { technology: "NVIDIA Omniverse", useCase: "AI for 3D simulation." },
      { technology: "Kaedim", useCase: "AI for 3D model generation." },
      { technology: "DeepMotion", useCase: "AI for animation." },
      { technology: "Luma AI", useCase: "3D reconstruction from images." },
      { technology: "Plask", useCase: "AI-powered motion capture." },
    ],
  },
  {
    number: 38,
    title: "AI for Fashion & Design",
    rows: [
      { technology: "Zeg.ai", useCase: "AI for fashion design." },
      { technology: "Vue.ai", useCase: "AI for visual commerce." },
      { technology: "Fashable", useCase: "AI-generated fashion trends." },
      { technology: "Stitch Fix AI", useCase: "Personalized styling." },
      { technology: "Heuritech", useCase: "AI for trend forecasting." },
    ],
  },
  {
    number: 39,
    title: "AI for Food Tech",
    rows: [
      { technology: "NotCo", useCase: "AI for plant-based food formulation." },
      { technology: "Spoon Guru", useCase: "AI for personalized nutrition." },
      { technology: "Gastrograph AI", useCase: "Flavor prediction." },
      { technology: "Plant Jammer", useCase: "AI recipe generator." },
      { technology: "Chef Watson (IBM)", useCase: "AI for cooking recipes." },
    ],
  },
  {
    number: 40,
    title: "AI for Real Estate",
    rows: [
      { technology: "Zillow Zestimate", useCase: "AI for home valuation." },
      { technology: "Skyline AI", useCase: "Predictive analytics for real estate." },
      { technology: "Cherre", useCase: "AI for property data insights." },
      { technology: "HouseCanary", useCase: "AI for market forecasting." },
      { technology: "Restb.ai", useCase: "AI for property image analysis." },
    ],
  },
  {
    number: 41,
    title: "AI for Insurance",
    rows: [
      { technology: "Lemonade", useCase: "AI for claims processing." },
      { technology: "Tractable", useCase: "AI for accident assessment." },
      { technology: "Shift Technology", useCase: "Fraud detection in insurance." },
      { technology: "Cape Analytics", useCase: "AI for property risk assessment." },
      { technology: "Zesty.ai", useCase: "AI for climate risk modeling." },
    ],
  },
  {
    number: 42,
    title: "AI for News & Media",
    rows: [
      { technology: "Reuters News Tracer", useCase: "AI for news verification." },
      { technology: "Narrativa", useCase: "AI-generated news articles." },
      { technology: "Automated Insights", useCase: "AI for financial reports." },
      { technology: "Heliograf (WaPo)", useCase: "Automated journalism." },
      { technology: "Articoolo", useCase: "AI content generation." },
    ],
  },
  {
    number: 43,
    title: "AI for Nonprofits & Social Good",
    rows: [
      { technology: "Benetech", useCase: "AI for accessibility." },
      { technology: "AI for Good (UN)", useCase: "AI solutions for global challenges." },
      { technology: "Rainforest Connection", useCase: "AI for wildlife conservation." },
      { technology: "Zooniverse", useCase: "Crowdsourced AI for science." },
      { technology: "One Concern", useCase: "AI for disaster resilience." },
    ],
  },
  {
    number: 44,
    title: "Emerging & Experimental AI",
    rows: [
      { technology: "NeuroSymbolic AI", useCase: "Combining neural networks and symbolic reasoning." },
      { technology: "Liquid Neural Networks", useCase: "Adaptive, continuously learning networks." },
      { technology: "Spiking Neural Networks", useCase: "Brain-inspired AI." },
      { technology: "Generative Agents (Stanford)", useCase: "Simulating human-like behavior." },
      { technology: "AI Dungeon", useCase: "Infinite storytelling with AI." },
    ],
  },
];

/* ─────────────────────────────────────────────
   TABLE OF CONTENTS (ordered 1–44)
───────────────────────────────────────────── */
const tocItems = catalogSections.map((s) => ({
  number: s.number,
  title: s.title,
  anchor: `section-${s.number}`,
}));

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const AITechnologiesCatalogPage = () => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (number) => {
    setOpenSections((prev) => ({ ...prev, [number]: !prev[number] }));
  };

  return (
    <>
      <style>{RESPONSIVE_CSS}</style>

      <div
        style={{
          background: "#24c7b8",
          minHeight: "100vh",
          paddingBottom: "50px",
          overflowX: "hidden",
        }}
      >
        {/* ── HERO ── */}
        <div style={{ width: "100%", position: "relative", overflow: "hidden" }}>
          <img
            src={heroImg}
            alt="AI Technologies Catalog"
            className="atc-hero-img"
            style={{
              width: "100%",
              height: "420px",
              objectFit: "cover",
              display: "block",
            }}
          />
          <div
            className="atc-hero-overlay"
            style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "100%",
              height: "90px",
              background: "rgba(36,199,184,0.72)",
              display: "flex",
              alignItems: "center",
              paddingLeft: "70px",
              boxSizing: "border-box",
            }}
          >
            <h1
              className="atc-hero-title"
              style={{
                color: "#fff",
                fontSize: "30px",
                fontWeight: "700",
                fontFamily: "Exo, sans-serif",
                margin: "0",
              }}
            >
              300+ AI, GenAI, LLM &amp; AI Agent Technologies — The Ultimate Catalog
            </h1>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div
          className="atc-main-wrap"
          style={{
            width: "85%",
            maxWidth: "1700px",
            margin: "35px auto",
            background: "#f3f3f3",
            padding: "32px",
            boxSizing: "border-box",
          }}
        >
          <div
            className="atc-banner-box"
            style={{
              background: "#04538f",
              borderRadius: "24px",
              padding: "22px 28px",
              marginBottom: "24px",
              boxSizing: "border-box",
            }}
          >
            {/* Heading */}
            <h1
              className="atc-banner-heading"
              style={{
                color: "#111",
                fontSize: "35px",
                lineHeight: "1.2",
                fontWeight: "800",
                margin: "0 0 12px",
                fontFamily: "Exo, sans-serif",
              }}
            >
              300+ AI, GenAI, LLM & AI Agent Technologies — The Ultimate Catalog
            </h1>

            {/* Description */}
            <p
              style={{
                color: "#fff",
                fontSize: "15px",
                lineHeight: "1.5",
                marginBottom: "14px",
              }}
            >
              From foundational ML frameworks to Generative AI, LLM tooling, Agents, Vision, Speech, and Cloud AI — this long-form catalog covers 300+ technologies with practical use cases for enterprise adoption.
            </p>

            {/* Meta Row */}
            <div
              className="atc-meta-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                marginTop: "10px",
                marginBottom: "14px",
              }}
            >
              <p
                style={{
                  color: "#fff",
                  fontSize: "13px",
                  margin: 0,
                }}
              >
                📅 Updated: 2025-08-16
              </p>

              <p
                style={{
                  color: "#fff",
                  fontSize: "13px",
                  margin: 0,
                }}
              >
                📄 Downloadable PDF available
              </p>
            </div>

            {/* Button */}
            <a
              href="/pdfs/ai-catalog.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="atc-download-btn"
              style={{
                display: "inline-block",
                background: "#fff",
                color: "#04538f",
                padding: "8px 16px",
                borderRadius: "10px",
                textDecoration: "none",
                fontWeight: "700",
                fontSize: "13px",
              }}
            >
              Download PDF
            </a>
          </div>

          {/* ── MAIN CONTENT LAYOUT ── */}
          <div
            className="atc-layout"
            style={{
              display: "grid",
              gridTemplateColumns: "320px 1fr",
              gap: "28px",
              alignItems: "start",
            }}
          >
            {/* LEFT SIDEBAR */}
            <div
              className="atc-sidebar"
              style={{
                background: "#fff",
                borderRadius: "20px",
                border: "1px solid #ddd",
                padding: "24px",
                boxSizing: "border-box",
                position: "sticky",
                top: "20px",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "800",
                  marginBottom: "22px",
                  color: "#04538f",
                  fontFamily: "Exo, sans-serif",
                }}
              >
                Contents
              </h2>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {tocItems.map((item) => (
                  <a
                    key={item.number}
                    href={`#${item.anchor}`}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      textDecoration: "none",
                      color: "#111",
                      fontSize: "14px",
                      lineHeight: "1.7",
                      fontWeight: "500",
                    }}
                  >
                    <span
                      style={{
                        color: "#4ea6ea",
                        fontSize: "12px",
                        marginTop: "4px",
                        flexShrink: 0,
                      }}
                    >
                      ◆
                    </span>

                    <span>
                      {item.number}. {item.title}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* RIGHT CONTENT */}
            <div style={{ minWidth: 0 }}>
              {/* HOW TO USE */}
              <div
                style={{
                  background: "#fff",
                  borderRadius: "18px",
                  padding: "20px 24px",
                  marginBottom: "22px",
                  borderLeft: "6px solid #04538f",
                  boxSizing: "border-box",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    color: "#111",
                    lineHeight: "1.7",
                  }}
                >
                  <strong>How to use:</strong> Click a section to collapse/expand.
                  Tables are horizontally scrollable on mobile.
                </p>
              </div>

              {/* ACCORDIONS */}
              {catalogSections.map((section) => (
                <div
                  key={section.number}
                  id={`section-${section.number}`}
                  style={{
                    marginBottom: "12px",
                    scrollMarginTop: "20px",
                  }}
                >
                  {/* HEADING */}
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "20px",
                      border: "1px solid #ddd",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="atc-accordion-head"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "18px",
                        padding: "22px 24px",
                        boxSizing: "border-box",
                      }}
                    >
                      {/* ONLY DIAMOND CLICKABLE */}
                      <div
                        className="atc-diamond"
                        onClick={() => toggleSection(section.number)}
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          background: "#dff0fd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          cursor: "pointer",
                        }}
                      >
                        <span
                          style={{
                            color: "#4ea6ea",
                            fontSize: "14px",
                            fontWeight: "700",
                            userSelect: "none",
                          }}
                        >
                          ◆
                        </span>
                      </div>

                      {/* TITLE */}
                      <h2
                        className="atc-section-title"
                        style={{
                          margin: 0,
                          fontSize: "22px",
                          lineHeight: "1.4",
                          color: "#111",
                          fontWeight: "800",
                          fontFamily: "Exo, sans-serif",
                          minWidth: 0,
                          wordBreak: "break-word",
                        }}
                      >
                        {section.number}. {section.title}
                      </h2>
                    </div>

                    {/* OPEN CONTENT */}
                    {openSections[section.number] && (
                      <div
                        style={{
                          borderTop: "1px solid #e5e5e5",
                          padding: "22px",
                          background: "#fff",
                          boxSizing: "border-box",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#555",
                            marginBottom: "18px",
                            lineHeight: "1.8",
                          }}
                        >
                          Comprehensive list of technologies under{" "}
                          <strong>
                            {section.number}. {section.title}
                          </strong>{" "}
                          sourced from Surens Inffotek's curated research.
                        </p>

                        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                          <table
                            className="atc-table"
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              minWidth: "700px",
                              background: "#fff",
                              borderRadius: "14px",
                              overflow: "hidden",
                            }}
                          >
                            <thead>
                              <tr>
                                <th
                                  className="atc-th"
                                  style={{
                                    background: "#04538f",
                                    color: "#fff",
                                    padding: "16px",
                                    textAlign: "center",
                                    fontSize: "15px",
                                    border: "1px solid #0b6bae",
                                    fontWeight: "700",
                                  }}
                                >
                                  Sub-category
                                </th>

                                <th
                                  className="atc-th"
                                  style={{
                                    background: "#04538f",
                                    color: "#fff",
                                    padding: "16px",
                                    textAlign: "center",
                                    fontSize: "15px",
                                    border: "1px solid #0b6bae",
                                    fontWeight: "700",
                                  }}
                                >
                                  Technology
                                </th>

                                <th
                                  className="atc-th"
                                  style={{
                                    background: "#04538f",
                                    color: "#fff",
                                    padding: "16px",
                                    textAlign: "center",
                                    fontSize: "15px",
                                    border: "1px solid #0b6bae",
                                    fontWeight: "700",
                                  }}
                                >
                                  Use Case
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {section.rows.map((row, idx) => (
                                <tr
                                  key={idx}
                                  style={{
                                    background:
                                      idx % 2 === 0 ? "#fff" : "#f7f7f7",
                                  }}
                                >
                                  <td
                                    className="atc-td"
                                    style={{
                                      padding: "16px",
                                      border: "1px solid #e5e5e5",
                                      fontSize: "14px",
                                      lineHeight: "1.7",
                                      textAlign: "center",
                                    }}
                                  >
                                    {section.title}
                                  </td>

                                  <td
                                    className="atc-td"
                                    style={{
                                      padding: "16px",
                                      border: "1px solid #e5e5e5",
                                      fontSize: "14px",
                                      lineHeight: "1.7",
                                      fontWeight: "700",
                                      textAlign: "center",
                                    }}
                                  >
                                    {row.technology}
                                  </td>

                                  <td
                                    className="atc-td"
                                    style={{
                                      padding: "16px",
                                      border: "1px solid #e5e5e5",
                                      fontSize: "14px",
                                      lineHeight: "1.7",
                                      textAlign: "center",
                                    }}
                                  >
                                    {row.useCase}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── FOOTER COPY ── */}
          <p
            style={{
              fontSize: "13px",
              color: "#666",
              textAlign: "center",
              marginTop: "30px",
              marginBottom: "40px",
              borderTop: "1px solid #ddd",
              paddingTop: "20px",
            }}
          >
            © 2025 Surens Inffotek. All rights reserved. For partnerships and enterprise
            deployments, contact us via the website.
          </p>

          {/* ── NAVIGATION ── */}
          <div
            className="atc-nav-row"
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
            }}
          >
            {/* Previous post */}
            <div
              className="atc-prev-next-box"
              style={{
                flex: 1,
                minWidth: "260px",
                border: "2px solid #fff",
                padding: "28px",
                background: "#24c7b8",
                boxSizing: "border-box",
              }}
            >
              <h2
                style={{
                  fontSize: "18px",
                  lineHeight: "1.5",
                  color: "#10213a",
                  fontWeight: "700",
                  marginBottom: "16px",
                  fontFamily: "Exo, sans-serif",
                }}
              >
                Future of RPA as per Analysts, Current Challenges of RPA and Its Applications
              </h2>
              <button
                onClick={() => navigate("/blog/rpa-future")}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#d4d4d4",
                  fontSize: "17px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                ← Previous post
              </button>
            </div>

            {/* Next post */}
            <div
              className="atc-prev-next-box"
              style={{
                flex: 1,
                minWidth: "260px",
                border: "2px solid #fff",
                padding: "28px",
                background: "#24c7b8",
                boxSizing: "border-box",
              }}
            >
              <h2
                style={{
                  fontSize: "18px",
                  lineHeight: "1.5",
                  color: "#10213a",
                  fontWeight: "700",
                  marginBottom: "16px",
                  fontFamily: "Exo, sans-serif",
                }}
              >
                Automating Purchase &amp; Sales Invoice Entry into SAP: A Research Guide to
                Document AI, APIs, and LLMs
              </h2>
              <button
                onClick={() => navigate("/blogs/sap-automation")}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#d4d4d4",
                  fontSize: "17px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Next post →
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AITechnologiesCatalogPage;
